import React, { useState, useEffect } from 'react';
import { Package, Plus, Edit, Trash2, X, Save, Loader2, Eye, EyeOff } from 'lucide-react';
import { getAdminServices, updateService, createService, deleteService } from '../../utils/adminApi';
import { useAdmin } from '../../contexts/AdminContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const ServicesManagement = () => {
  const { token } = useAdmin();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: { en: '', es: '', de: '' },
    description: { en: '', es: '', de: '' },
    price: 0,
    recurring: false,
    active: true
  });

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const data = await getAdminServices();
      setServices(data.services || []);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: { en: '', es: '', de: '' },
      description: { en: '', es: '', de: '' },
      price: 0,
      recurring: false,
      active: true
    });
    setEditingService(null);
  };

  const openEditModal = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name || { en: '', es: '', de: '' },
      description: service.description || { en: '', es: '', de: '' },
      price: service.price || 0,
      recurring: service.recurring || false,
      active: service.active !== false
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.en || !formData.price) {
      toast.error('Name (English) and price are required');
      return;
    }

    setSaving(true);
    try {
      if (editingService) {
        await updateService(token, editingService.service_id, formData);
        toast.success('Service updated successfully');
      } else {
        await createService(token, formData);
        toast.success('Service created successfully');
      }
      setShowModal(false);
      resetForm();
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId, serviceName) => {
    if (!window.confirm(`Are you sure you want to delete "${serviceName}"?`)) return;
    
    try {
      await deleteService(token, serviceId);
      toast.success('Service deleted');
      loadServices();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const handleToggleActive = async (service) => {
    try {
      await updateService(token, service.service_id, { active: !service.active });
      toast.success(`Service ${service.active ? 'deactivated' : 'activated'}`);
      loadServices();
    } catch (error) {
      toast.error('Failed to update service');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="services-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Package className="w-7 h-7 text-[#D4AF37]" />
            Services Management
          </h1>
          <p className="text-[#9CA3AF] mt-1">Manage your service offerings and pricing</p>
        </div>
        <Button
          onClick={openCreateModal}
          className="bg-[#D4AF37] hover:bg-[#c9a432] text-[#141619]"
          data-testid="add-service-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Service
        </Button>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service) => (
          <div
            key={service.service_id}
            className="bg-[#1a1d21] rounded-xl border border-[#2a2d32] p-6 hover:border-[#D4AF37]/50 transition-all"
            data-testid={`service-card-${service.service_id}`}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white mb-1">{service.name?.en || 'Untitled'}</h3>
                <span className="text-sm text-[#6B7280]">{service.category}</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                service.active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                {service.active !== false ? 'Active' : 'Inactive'}
              </span>
            </div>
            
            <p className="text-[#9CA3AF] text-sm mb-4 line-clamp-2">
              {service.description?.en || 'No description'}
            </p>
            
            <div className="flex items-center justify-between mb-4">
              <span className="text-2xl font-bold text-[#D4AF37]">
                €{service.price?.toFixed(2)}
              </span>
              {service.recurring && (
                <span className="text-xs text-[#6B7280] bg-[#2a2d32] px-2 py-1 rounded">/month</span>
              )}
            </div>
            
            <div className="flex items-center gap-2 pt-4 border-t border-[#2a2d32]">
              <button
                onClick={() => openEditModal(service)}
                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-[#2a2d32] hover:bg-[#3a3d42] rounded-lg transition-colors text-white text-sm"
                data-testid={`edit-service-${service.service_id}`}
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => handleToggleActive(service)}
                className="p-2 hover:bg-[#2a2d32] rounded-lg transition-colors"
                title={service.active ? 'Deactivate' : 'Activate'}
              >
                {service.active !== false ? (
                  <EyeOff className="w-4 h-4 text-[#9CA3AF]" />
                ) : (
                  <Eye className="w-4 h-4 text-[#9CA3AF]" />
                )}
              </button>
              <button
                onClick={() => handleDelete(service.service_id, service.name?.en)}
                className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                data-testid={`delete-service-${service.service_id}`}
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit/Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1d21] rounded-xl border border-[#2a2d32] w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#2a2d32]">
              <h2 className="text-xl font-bold text-white">
                {editingService ? 'Edit Service' : 'Create New Service'}
              </h2>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="p-2 hover:bg-[#2a2d32] rounded-lg"
              >
                <X className="w-5 h-5 text-[#9CA3AF]" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Name Fields */}
              <div>
                <h3 className="text-sm font-medium text-[#D4AF37] mb-3">Service Name</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-[#6B7280] mb-1">English *</label>
                    <Input
                      value={formData.name.en}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        name: { ...prev.name, en: e.target.value }
                      }))}
                      className="bg-[#141619] border-[#2a2d32] text-white"
                      placeholder="Service name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#6B7280] mb-1">Spanish</label>
                    <Input
                      value={formData.name.es}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        name: { ...prev.name, es: e.target.value }
                      }))}
                      className="bg-[#141619] border-[#2a2d32] text-white"
                      placeholder="Nombre del servicio"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#6B7280] mb-1">German</label>
                    <Input
                      value={formData.name.de}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        name: { ...prev.name, de: e.target.value }
                      }))}
                      className="bg-[#141619] border-[#2a2d32] text-white"
                      placeholder="Dienstname"
                    />
                  </div>
                </div>
              </div>

              {/* Description Fields */}
              <div>
                <h3 className="text-sm font-medium text-[#D4AF37] mb-3">Description</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-[#6B7280] mb-1">English</label>
                    <textarea
                      value={formData.description.en}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        description: { ...prev.description, en: e.target.value }
                      }))}
                      className="w-full px-3 py-2 bg-[#141619] border border-[#2a2d32] rounded-lg text-white placeholder-[#6B7280] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
                      rows={3}
                      placeholder="Service description..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#6B7280] mb-1">Spanish</label>
                    <textarea
                      value={formData.description.es}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        description: { ...prev.description, es: e.target.value }
                      }))}
                      className="w-full px-3 py-2 bg-[#141619] border border-[#2a2d32] rounded-lg text-white placeholder-[#6B7280] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
                      rows={3}
                      placeholder="Descripción del servicio..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#6B7280] mb-1">German</label>
                    <textarea
                      value={formData.description.de}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        description: { ...prev.description, de: e.target.value }
                      }))}
                      className="w-full px-3 py-2 bg-[#141619] border border-[#2a2d32] rounded-lg text-white placeholder-[#6B7280] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
                      rows={3}
                      placeholder="Dienstbeschreibung..."
                    />
                  </div>
                </div>
              </div>

              {/* Price & Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#9CA3AF] mb-2">Price (€) *</label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    className="bg-[#141619] border-[#2a2d32] text-white"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="flex items-center gap-6 pt-8">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.recurring}
                      onChange={(e) => setFormData(prev => ({ ...prev, recurring: e.target.checked }))}
                      className="w-4 h-4 rounded border-[#2a2d32] bg-[#141619] text-[#D4AF37] focus:ring-[#D4AF37]"
                    />
                    <span className="text-sm text-[#9CA3AF]">Monthly recurring</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                      className="w-4 h-4 rounded border-[#2a2d32] bg-[#141619] text-[#D4AF37] focus:ring-[#D4AF37]"
                    />
                    <span className="text-sm text-[#9CA3AF]">Active</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-6 border-t border-[#2a2d32]">
              <Button
                variant="outline"
                onClick={() => { setShowModal(false); resetForm(); }}
                className="flex-1 border-[#2a2d32] text-white hover:bg-[#2a2d32]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-[#D4AF37] hover:bg-[#c9a432] text-[#141619]"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {editingService ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesManagement;
